import React, { useRef } from "react";

const toolbarButtons = [
  { label: "B", marker: "**", ariaLabel: "Bold" },
  { label: "I", marker: "*", ariaLabel: "Italic" }
];

const RichTextArea = ({
  value,
  onChange,
  placeholder = "",
  className = "",
  containerClassName = "",
  rows = "",
  error = "",
  showError = false,
  fullWidth = false,
  disabled = false
}) => {
  const textareaRef = useRef(null);

  const applyFormatting = (marker) => {
    const textarea = textareaRef.current;

    if (!textarea || disabled) {
      return;
    }

    const start = textarea.selectionStart ?? 0;
    const end = textarea.selectionEnd ?? 0;
    const currentValue = textarea.value ?? "";
    const markerLength = marker.length;

    let newValue = currentValue;
    let newSelectionStart = start;
    let newSelectionEnd = end;
    const selectedText = currentValue.slice(start, end);
    const selectionWrapped = selectedText.startsWith(marker) &&
      selectedText.endsWith(marker) &&
      selectedText.length >= markerLength * 2;
    const hasOuterMarkers = start >= markerLength &&
      currentValue.slice(start - markerLength, start) === marker &&
      currentValue.slice(end, end + markerLength) === marker;

    if (selectionWrapped) {
      const unwrappedText = selectedText.slice(markerLength, selectedText.length - markerLength);
      newValue = currentValue.slice(0, start) + unwrappedText + currentValue.slice(end);
      newSelectionStart = start;
      newSelectionEnd = start + unwrappedText.length;
    } else if (start !== end && hasOuterMarkers) {
      newValue = currentValue.slice(0, start - markerLength) +
        selectedText +
        currentValue.slice(end + markerLength);
      newSelectionStart = start - markerLength;
      newSelectionEnd = end - markerLength;
    } else if (start !== end) {
      newValue = currentValue.slice(0, start) +
        marker +
        selectedText +
        marker +
        currentValue.slice(end);
      newSelectionStart = start + markerLength;
      newSelectionEnd = end + markerLength;
    } else {
      newValue = currentValue.slice(0, start) + marker + marker + currentValue.slice(end);
      newSelectionStart = start + markerLength;
      newSelectionEnd = newSelectionStart;
    }

    onChange(newValue);

    window.requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.focus();
        textareaRef.current.setSelectionRange(newSelectionStart, newSelectionEnd);
      }
    });
  };

  return (
    <div className={`${fullWidth ? "w-full" : "inline-flex"} flex-col items-start ${containerClassName}`}>
      <div className="flex gap-2 mb-2">
        {toolbarButtons.map(({ label, marker, ariaLabel }) => (
          <button
            key={marker}
            type="button"
            onClick={() => applyFormatting(marker)}
            aria-label={ariaLabel}
            className={`px-2 py-1 border border-gray-300 rounded text-sm font-semibold ${disabled ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-100"}`}
            disabled={disabled}
          >
            {label}
          </button>
        ))}
      </div>
      <textarea
        ref={textareaRef}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className={`editable-field ${className} ${error && "error"}`}
        disabled={disabled}
        rows={rows}
      />
      {showError && error && (
        <p className="text-red-500 text-sm mt-1">{error}</p>
      )}
    </div>
  );
};

export default RichTextArea;
